/* eslint-disable @typescript-eslint/no-explicit-any */
import { download } from "../../../utils/util";
import adminApiClient from "../../../api/admin";
import { useEffect, useState, useRef, useCallback } from "react";
import {
  FaEdit,
  FaRegClock,
  FaRegHeart,
  FaVolumeUp,
  FaVolumeMute,
  FaRegPauseCircle,
  FaRegPlayCircle,
  FaTrash,
  FaRegTrashAlt,
  FaRegEye,
  FaStar,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import KlydoFrames from "../../KlydoFrames";
import ChooseFramesDialog from "./ChooseFramesDialog";
import { Klydo } from "../../../Types";
import ButtonLoader from "../../../components/Widgets/ButtonLoader";
import Editable from "../../../components/Widgets/Editable";
import SimpleSwitch from "../../../components/Widgets/SimpleSwitch";
import TimeControl from "../../../components/Widgets/TimeControl";
import TagList from "../../../components/Widgets/TagList";
import ClockView from "../../../components/Widgets/ClockView";
import ConfirmationSwitch from "../../ConfirmationSwitch";
import useKlydo from "../../../components/hooks/useKlydo";
import useKlydoMutation from "../hooks/useKlydoMutation";
import useSealKlydoMutation from "../hooks/useSealKlydoMutation";
import ConfirmationModal from "../../ConfirmationModal";
import { extractApiErrorMessage } from "../../../utils/util";
import useUserQuery from "../../Collection/hooks/useUserQuery";
import {
  Theme,
  SoundMode,
  AlarmType,
  PublicationReviewStates,
  PublicationRequestSource,
  KlydoType,
} from "../../../api/klydo";
import DSChip from "../../system-design/DSChip";
import DSSectionHeading from "../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../system-design/sectionCardSx";
import DSCheckBox from "../../system-design/DSCheckBox";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSIconButton from "../../system-design/DSIconButton";
import DSInput from "../../system-design/DSInput";
import UserDropdown from "../../Collection/components/UserDropdown";
import useUploadKlydoLoop, {
  isValidLoopFile,
} from "../hooks/useUploadKlydoLoop";
import DSDialog from "../../system-design/DSDialog";
import DSLabelTypography from "../../system-design/DSLabelTypography";
import useUploadChimeMutation from "../hooks/useUploadChimeMutation";
import useUploadStrikeMutation from "../hooks/useUploadStrikeMutation";
import useUploadAlarmMutation from "../hooks/useUploadAlarmMutation";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSTooltip from "../../system-design/DSTooltip";
import useKlydoScheduledEvent from "../hooks/useKlydoScheduledEvent";
import useRemoveKlydoFromScheduler from "../hooks/useRemoveKlydoFromScheduler";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSFormControl from "../../system-design/DSFormControl";
import DSInputLabel from "../../system-design/DSInputLabel";
import { KLYDO_WHITE } from "../../../consts/colors";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ShareIcon from "@mui/icons-material/Share";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import DSTextField from "../../system-design/DSTextField";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSErrorButton from "../../system-design/DSErrorButton";
import DSButton from "../../system-design/DSButton";
import AudioPlayer from "../../../components/AudioPlayer/AudioPlayer";
import { getChimeDisplayName } from "../../../utils/chimeUtils";
import useGeneratePreviewVideoMutation from "../../../api/hooks/useGeneratePreviewVideoMutation";
import PublishDateDialog, { PublishType } from "./PublishDateDialog";
import { Schedule } from "@mui/icons-material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PendulumCard from "./KlydoInfo/PendulumCard";
import useCreateKlydoLabelsBulkMutation from "../hooks/useCreateKlydoLabelsBulkMutation";
import useKlydoDeleteCount from "../../../components/hooks/useKlydoDeleteCount";
import { useKlydoRatingStats } from "../hooks/useKlydoRatingStats";
import UnifiedCalendarEventModal from "../../CalendarEvent/components/UnifiedCalendarEventModal";

type KlydoModalWrapperProps = {
  klydoId?: string;
  row?: any;
  setShowModal: (show: boolean) => void;
};

/**
 * How long after its go-live date a klydo cannot be turned private, because the
 * server-side publish cron would undo it. Mirrors PUBLISH_WINDOW_MS in klydo-api's
 * PublishScheduledKlydosService — the two must move together.
 */
const PUBLISH_WINDOW_HOURS = 2;

function KlydoModal(props: {
  klydo: Klydo;
  setShowModal: (show: boolean) => void;
}) {
  const { setShowModal, klydo } = props;
  // Served publicly right now. The stored gate — never derived from a date.
  const isPublic = !!klydo.public?.status;
  // The go-live date that was ASKED for. Answers "on what date?" only; a value
  // here can be in the future, which is not the same as being public.
  const requestedPublishTimestamp =
    klydo.public?.reviewStatus?.requestPayload?.timestamp ?? null;
  // The date was set by an admin on a klydo that never made a publication
  // request, not asked for by the artist. Absent means artist — see
  // PublicationRequestSource — so this is only ever true for records the
  // backoffice itself created.
  const isAdminScheduled =
    klydo.public?.reviewStatus?.requestPayload?.source ===
    PublicationRequestSource.Admin;
  // Mirrors PUBLISH_WINDOW_MS in klydo-api's PublishScheduledKlydosService. If that
  // window changes, this has to change with it.
  const isInsidePublishWindow =
    !!requestedPublishTimestamp &&
    Date.now() - requestedPublishTimestamp <
      PUBLISH_WINDOW_HOURS * 60 * 60 * 1000;
  // What the switch reflects: public NOW, or scheduled to become public. Not the
  // same question as `isPublic`, and deliberately so — the switch is the only way
  // to CANCEL a scheduled publication, and `togglePublicStatus(false)` is what
  // clears both fields. Reading `isPublic` here would render a future-dated klydo
  // as off, next to a label reading "Will be public on …", with no way back.
  const isPublicOrScheduled = isPublic || !!requestedPublishTimestamp;
  /**
   * Not served yet, but its approved go-live date is still ahead — the api's
   * hourly publish cron WILL open the gate on that date.
   *
   * Mirrors `willPublishBeforeEvent` in klydo-api's
   * `CalendarEventService.getEventsWithNonPublicKlydos`, which is the authority on
   * whether a scheduled klydo is acceptable in a calendar event. Both halves are
   * load-bearing: without `Approved` the cron never sees the klydo, and without
   * `SealedKlydo` it can never be served even once `status` flips.
   */
  const willBecomePublic =
    !isPublic &&
    klydo.type === KlydoType.Sealed &&
    klydo.public?.reviewStatus?.state === PublicationReviewStates.Approved &&
    !!requestedPublishTimestamp &&
    requestedPublishTimestamp > Date.now();
  /**
   * The earliest event day whose klydos are guaranteed live when it starts.
   *
   * The server anchors an event to midnight America/New_York and requires the
   * go-live to land a full cron period before that, so a klydo going public ON the
   * event day is too late — the day has already begun by the time the cron runs.
   * Hence the day AFTER the go-live date, which is the first one that always
   * clears the bound. Passed to the event modal so the date cannot be picked
   * wrong, rather than surfacing later as a monitor alert.
   */
  const goLiveDate =
    willBecomePublic && requestedPublishTimestamp
      ? new Date(requestedPublishTimestamp)
      : null;
  const earliestSchedulableDate = goLiveDate
    ? new Date(new Date(goLiveDate).setHours(24, 0, 0, 0))
    : new Date();
  const { data: deleteCountData } = useKlydoDeleteCount(klydo.id, !!klydo.id);
  const { data: ratingStats } = useKlydoRatingStats(klydo.id, !!klydo.id);
  const [normalizeLoop, setNormalizeLoop] = useState(true);
  const [normalizeChime, setNormalizeChime] = useState(true);
  const [normalizeStrike, setNormalizeStrike] = useState(true);
  // The file waits here until the admin confirms the dialog. The normalize flag
  // changes the upload itself, so it must be chosen before the upload. The
  // sound mode does not: the API applies it when it promotes the loop. It is
  // sent with `loopUrl` in one update, so the promotion uses the mode chosen
  // here, not the old one.
  const [pendingLoopFile, setPendingLoopFile] = useState<File | null>(null);
  const [dialogSoundMode, setDialogSoundMode] = useState<SoundMode>(
    SoundMode.OFF,
  );
  const [loopSoundMode, setLoopSoundMode] = useState<SoundMode>(SoundMode.OFF);
  const [loopInputKey, setLoopInputKey] = useState(0);
  const {
    uploadFile,
    uploadResponse,
    isLoadingUrl,
    errorMessage,
    resetUploadResponse,
  } = useUploadKlydoLoop();
  const { mutateAsync: uploadChime, isPending: isUploadingChime } =
    useUploadChimeMutation();
  const [chimeUploadResponse, setChimeUploadResponse] = useState<{
    url: string;
    fileName: string;
    chimesDurationInSeconds?: number;
  } | null>(null);
  const [chimeErrorMessage, setChimeErrorMessage] = useState<string>("");
  const { mutateAsync: uploadStrike, isPending: isUploadingStrike } =
    useUploadStrikeMutation();
  const [strikeUploadResponse, setStrikeUploadResponse] = useState<{
    url: string;
    fileName: string;
  } | null>(null);
  const [strikeErrorMessage, setStrikeErrorMessage] = useState<string>("");
  const { mutateAsync: uploadAlarm, isPending: isUploadingAlarm } =
    useUploadAlarmMutation();
  const [alarmUploadResponse, setAlarmUploadResponse] = useState<{
    url: string;
    fileName: string;
    alarmDurationInSeconds?: number;
  } | null>(null);
  const [alarmErrorMessage, setAlarmErrorMessage] = useState<string>("");
  const { mutateAsync: updateKlydo, isPending: isUpdateingKlydo } =
    useKlydoMutation(klydo.id, {
      onSuccess: (_data, variables) => {
        // Clear the freshly-uploaded loop state once it's saved: the server has
        // promoted the temp asset into the constant folder and deleted the temp
        // source, so re-sending this now-dead temp url (a second Save click)
        // would 404. The refetched klydo carries the promoted loop.
        if (variables.loopUrl) resetUploadResponse();
        if (variables.chimesUrls) setChimeUploadResponse(null);
        if (variables.strikesUrls) setStrikeUploadResponse(null);
        if (variables.alarmUrls) setAlarmUploadResponse(null);
      },
    });
  const { mutateAsync: createLabelsBulk } = useCreateKlydoLabelsBulkMutation();
  const [licensingEntries, setLicensingEntries] = useState<
    Array<{ expirationDate: string; description: string }>
  >(klydo.licensing || []);
  const [publishDateTypeDialog, setPublishDateTypeDialog] = useState<
    PublishType | ""
  >("");
  const [showSealConfirmation, setShowSealConfirmation] = useState(false);
  const [sealError, setSealError] = useState<string | null>(null);
  const { mutateAsync: sealKlydo, isPending: isSealing } = useSealKlydoMutation(
    klydo.id,
  );
  const [showAddToKlydoEvent, setShowAddToKlydoEvent] = useState(false);
  const { data: scheduledEvent } = useKlydoScheduledEvent(klydo.id);
  const [showRemoveFromScheduler, setShowRemoveFromScheduler] = useState(false);
  const [removeFromSchedulerError, setRemoveFromSchedulerError] = useState("");
  const { mutate: removeFromScheduler, isPending: isRemovingFromScheduler } =
    useRemoveKlydoFromScheduler(klydo.id);

  const scheduledEventDate = scheduledEvent
    ? `${String(scheduledEvent.date.day).padStart(2, "0")}/${String(scheduledEvent.date.month).padStart(2, "0")}/${String(scheduledEvent.date.year).slice(-2)}`
    : "";

  const handleRemoveFromScheduler = () => {
    // ConfirmationModal's "Yes" has no pending state, so without this a slow
    // request invites a second click and a second mutate.
    if (!scheduledEvent || isRemovingFromScheduler) return;
    setRemoveFromSchedulerError("");
    removeFromScheduler(scheduledEvent, {
      onSuccess: () => setShowRemoveFromScheduler(false),
      onError: (error) => {
        setShowRemoveFromScheduler(false);
        // Removal needs admin rights while merely seeing the button needs only
        // viewer rights, so a 403 here is reachable and must not fail silently.
        setRemoveFromSchedulerError(
          error instanceof Error
            ? error.message
            : "Failed to remove from scheduler",
        );
      },
    });
  };

  const [cahngeName, setCahngeName] = useState(false);
  const [klydoName, setKlydoName] = useState(klydo.name);
  const [changeSubtitle, setChangeSubtitle] = useState(false);
  const [klydoSubtitle, setKlydoSubtitle] = useState(klydo.subtitle ?? "");
  const [cahngeCreator, setCahngeCreator] = useState(false);
  const [creatorId, setCreatorId] = useState(klydo.creator);
  const [showFrames, setShowFrames] = useState(false);
  const [showChooseFrames, setShowChooseFrames] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { data: author } = useUserQuery(klydo.creator);
  const navigate = useNavigate();
  const [isPaused, setIsPaused] = useState(false);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement | null }>({});
  const [playingChime, setPlayingChime] = useState<number | null>(null);

  const {
    mutate: generatePreviewVideo,
    isPending: isGenerating,
    error: videoError,
    isSuccess: videoSuccess,
    data: videoData,
  } = useGeneratePreviewVideoMutation();
  const [displayDuration, setDisplayDuration] = useState<number>(5);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string>("");
  const [videoCopied, setVideoCopied] = useState(false);

  useEffect(() => {
    if (videoSuccess && videoData?.videoUrl) {
      setGeneratedVideoUrl(videoData.videoUrl);
    }
  }, [videoSuccess, videoData]);

  const handleCopyVideoUrl = async () => {
    if (generatedVideoUrl) {
      try {
        await navigator.clipboard.writeText(generatedVideoUrl);
        setVideoCopied(true);
        setTimeout(() => setVideoCopied(false), 3000);
      } catch (error) {
        console.error("Failed to copy video URL:", error);
      }
    }
  };

  const deleteChime = async () => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.pause();
    });

    const updatedMetadata = { ...klydo.metadata };
    delete updatedMetadata.chimesFileName;
    delete updatedMetadata.chimesDurationInSeconds;

    await updateKlydo({
      chimesUrls: [],
      metadata: updatedMetadata,
    });

    setChimeUploadResponse(null);
    setChimeErrorMessage("");
  };

  const deleteStrike = async () => {
    await updateKlydo({ strikesUrls: [] });
    setStrikeUploadResponse(null);
    setStrikeErrorMessage("");
  };

  const deleteAlarm = async () => {
    const updatedMetadata = { ...klydo.metadata };
    delete updatedMetadata.alarmFileName;
    delete updatedMetadata.alarmDurationInSeconds;

    await updateKlydo({
      alarmUrls: [],
      metadata: updatedMetadata,
    });

    setAlarmUploadResponse(null);
    setAlarmErrorMessage("");
  };

  const dialsColor = klydo.theme.dialsColor || klydo.theme.handsColor;

  const showDials = klydo.dials ?? true;
  const updateTheme = (updateObject: Partial<Theme>) =>
    updateKlydo({ theme: { ...klydo.theme, ...updateObject } });

  const updateTags = async (tags: string[]) => {
    await Promise.all([
      createLabelsBulk({ texts: tags }),
      updateKlydo({ tags }),
    ]);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsPaused((prev) => !prev);
    }
  };

  const isUnlistedValidAction = () => {
    if (!klydo.unlisted && isPublic) {
      return "Not allowed to set unlisted for public klydos";
    }
    return "";
  };

  const isPublicdValidAction = () => {
    if (!isPublic && klydo.unlisted) {
      return "Not allowed to set public for unlisted klydos";
    }
    // A take-down inside the publish cron's window would be undone: the cron looks
    // for an approved request whose date passed within PUBLISH_WINDOW_MS, and a
    // take-down deliberately leaves the verdict at `approved`. Blocking the switch
    // for that window is what keeps the two consistent, and it is why the API no
    // longer has to erase the artist's requested date to hide it from the cron.
    if (isPublic && isInsidePublishWindow) {
      return `Just published — you can turn this Klydo private ${PUBLISH_WINDOW_HOURS} hours after its publication date`;
    }
    return "";
  };

  const toggleUnlistedStatus = async (status: boolean) => {
    updateKlydo({ unlisted: status });
  };

  const togglePublicStatus = async (status: boolean) => {
    if (!status) {
      // Closes the gate and leaves the requested date alone. The date is the
      // artist's, not a control flag, and the server-side cron only republishes a
      // date that passed within its own window — see `isPublicdValidAction`, which
      // is what stops a take-down inside that window.
      await updateKlydo({ public: { status: false } });
      setPublishDateTypeDialog("");
    } else {
      setPublishDateTypeDialog(PublishType.PUBLIC);
    }
  };

  const toggleGeneratedByAI = async (status: boolean) => {
    updateKlydo({
      metadata: {
        ...klydo.metadata,
        generatedByAI: status,
      },
    });
  };

  const toggleGreet = async (status: boolean) => {
    updateKlydo({ greet: status });
  };

  // Artist rating campaign. The whole artistsRating subdoc is replaced on each
  // change (the update does a top-level set), so always send the full object.
  const saveArtistsRating = (next: {
    active: boolean;
    startedAt?: string;
    endDate?: string;
  }) => {
    updateKlydo({ artistsRating: next });
  };

  // When activating with no dates set, default to now → one month from now.
  const defaultRatingDates = (current?: {
    startedAt?: string;
    endDate?: string;
  }) => {
    const now = new Date();
    const inOneMonth = new Date(now);
    inOneMonth.setMonth(inOneMonth.getMonth() + 1);
    return {
      startedAt: current?.startedAt ?? now.toISOString(),
      endDate: current?.endDate ?? inOneMonth.toISOString(),
    };
  };

  const toggleRatingKlydo = async (status: boolean) => {
    saveArtistsRating({
      active: status,
      startedAt: klydo.artistsRating?.startedAt,
      endDate: klydo.artistsRating?.endDate,
      ...(status && defaultRatingDates(klydo.artistsRating)),
    });
  };

  const handleRatingDateChange = (
    field: "startedAt" | "endDate",
    value: string,
  ) => {
    saveArtistsRating({
      active: klydo.artistsRating?.active ?? false,
      startedAt: klydo.artistsRating?.startedAt,
      endDate: klydo.artistsRating?.endDate,
      [field]: value ? new Date(value).toISOString() : undefined,
    });
  };

  // ISO string -> value for <input type="datetime-local"> (local time, no tz).
  const toDateTimeLocalValue = (iso?: string) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const handleLoopFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isValidLoopFile(file)) return;
    setNormalizeLoop(true);
    setDialogSoundMode(
      klydo.capabilitiesV2?.animationSound?.mode || SoundMode.OFF,
    );
    setPendingLoopFile(file);
  };

  const closeLoopDialog = () => {
    setPendingLoopFile(null);
    // Remount the file input so picking the same file again fires onChange.
    setLoopInputKey((key) => key + 1);
  };

  const confirmLoopUpload = () => {
    if (!pendingLoopFile) return;
    setLoopSoundMode(dialogSoundMode);
    uploadFile(pendingLoopFile, normalizeLoop);
    closeLoopDialog();
  };

  const handlePlayOnceConfigChange = (
    config: Partial<{ frequency: number | null; random: boolean | null }>,
  ) => {
    updateKlydo({
      capabilitiesV2: {
        ...klydo.capabilitiesV2,
        animationSound: {
          ...klydo.capabilitiesV2?.animationSound,
          playOnceConfig: {
            ...klydo.capabilitiesV2?.animationSound?.playOnceConfig,
            ...config,
          },
        },
      },
    });
  };

  const toggleAlarm = async (status: boolean) => {
    updateKlydo({
      capabilitiesV2: {
        ...klydo.capabilitiesV2,
        alarm: status ? { type: AlarmType.REMINDER } : undefined,
      },
    });
  };

  function getContrastYIQ(hexcolor: string) {
    if (!hexcolor) return "black";
    const r = parseInt(hexcolor.substring(1, 3), 16);
    const g = parseInt(hexcolor.substring(3, 5), 16);
    const b = parseInt(hexcolor.substring(5, 7), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "black" : "white";
  }

  function toMyTimeString(time: number): string {
    if (time < 100) return time + "s";
    if (time / 60 < 100) return Math.floor(time / 60).toLocaleString() + "m";
    if (time / 3600 < 100)
      return Math.floor(time / 3600).toLocaleString() + "h";
    return Math.floor(time / (24 * 3600)).toLocaleString() + "d";
  }

  const handlePlay = useCallback((index: number) => {
    Object.entries(audioRefs.current).forEach(([key, audio]) => {
      if (audio && parseInt(key) !== index) {
        audio.pause();
      }
    });
    setPlayingChime(index);
  }, []);

  return (
    <>
      <DSDialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          typography: "h5",
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        Klydo Info
        <DSIconButton aria-label="Close" onClick={() => setShowModal(false)}>
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "grid",
            gridTemplateColumns: { xxs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DSBox>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Info</DSSectionHeading>
              <DSCardContent>
                <DSBox>
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Klydo ID:</b> {klydo.id}
                    </DSTypography>
                  </DSBox>
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Friendly ID:</b> {klydo.id}
                    </DSTypography>
                  </DSBox>
                  {klydo.collectionId && (
                    <DSBox>
                      <DSTypography variant="body1">
                        <b>Collection ID:</b> {klydo.collectionId}
                      </DSTypography>
                    </DSBox>
                  )}
                  <DSBox sx={{ mb: 1.5 }}>
                    <DSTypography
                      variant="body1"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <b>Name:</b>
                      {cahngeName ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "500px",
                            minWidth: "200px",
                            maxWidth: "700px",
                          }}
                        >
                          <DSTextField
                            multiline
                            maxRows={2}
                            size="small"
                            fullWidth
                            sx={{ ml: 1.5 }}
                            value={klydoName}
                            onChange={(e) => setKlydoName(e.target.value)}
                          />
                          <ButtonLoader
                            variant="outline-dark"
                            title={<CheckIcon fontSize="small" />}
                            onClick={() => {
                              return updateKlydo({ name: klydoName });
                            }}
                          />
                          <DSIconButton
                            size="small"
                            onClick={() => {
                              setKlydoName(klydo.name);
                              setCahngeName(false);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </DSIconButton>
                        </div>
                      ) : (
                        <>
                          {klydoName}
                          <FaEdit
                            size={20}
                            style={{ marginLeft: 10 }}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setCahngeName(true);
                            }}
                          />
                        </>
                      )}
                    </DSTypography>
                  </DSBox>
                  <DSBox sx={{ mb: 1.5 }}>
                    <DSTypography
                      variant="body1"
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <b>Subtitle:</b>
                      {changeSubtitle ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "500px",
                            minWidth: "200px",
                            maxWidth: "700px",
                          }}
                        >
                          <DSTextField
                            size="small"
                            fullWidth
                            sx={{ ml: 1.5 }}
                            value={klydoSubtitle}
                            onChange={(e) => setKlydoSubtitle(e.target.value)}
                          />
                          <ButtonLoader
                            variant="outline-dark"
                            title={<CheckIcon fontSize="small" />}
                            onClick={() => {
                              return updateKlydo({ subtitle: klydoSubtitle });
                            }}
                          />
                          <DSIconButton
                            size="small"
                            onClick={() => {
                              setKlydoSubtitle(klydo.subtitle ?? "");
                              setChangeSubtitle(false);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </DSIconButton>
                        </div>
                      ) : (
                        <>
                          <span style={{ marginLeft: "8px" }}>
                            {klydoSubtitle || (
                              <em style={{ opacity: 0.6 }}>None</em>
                            )}
                          </span>
                          <FaEdit
                            size={20}
                            style={{ marginLeft: 10 }}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              setChangeSubtitle(true);
                            }}
                          />
                        </>
                      )}
                    </DSTypography>
                  </DSBox>
                  <DSBox>
                    <div style={{ display: "flex", flexDirection: "row" }}>
                      <div
                        title="Total likes"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginRight: 12,
                        }}
                      >
                        <FaRegHeart size={20} />
                        <p
                          style={{
                            fontSize: 12,
                            position: "relative",
                            top: -4,
                          }}
                        >
                          {(klydo.stats?.favorites || 0).toLocaleString()}
                        </p>
                      </div>
                      <div
                        title="Total views"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginRight: 12,
                        }}
                      >
                        <FaRegEye size={20} />
                        <p
                          style={{
                            fontSize: 12,
                            position: "relative",
                            top: -4,
                          }}
                        >
                          {(klydo.stats?.viewed || 0).toLocaleString()}
                        </p>
                      </div>
                      {!!klydo?.stats?.time && (
                        <div
                          title="Total viewing time"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <FaRegClock />
                          <p style={{ fontSize: 12 }}>
                            {toMyTimeString(klydo.stats.time)}
                          </p>
                        </div>
                      )}
                      <div
                        title="Average rating (votes)"
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          marginLeft: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            marginRight: 6,
                          }}
                        >
                          <FaStar size={20} color="#FFC107" />
                          <p
                            style={{
                              fontSize: 12,
                              position: "relative",
                              top: -4,
                            }}
                          >
                            {ratingStats && ratingStats.count > 0
                              ? ratingStats.average.toFixed(1)
                              : "-"}
                          </p>
                        </div>
                        <p style={{ fontSize: 12 }}>
                          ({ratingStats?.count ?? 0} votes)
                        </p>
                      </div>
                      {!!deleteCountData?.deleted && (
                        <div
                          title="Times deleted by users"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            marginLeft: 12,
                          }}
                        >
                          <FaRegTrashAlt size={20} />
                          <p
                            style={{
                              fontSize: 12,
                              position: "relative",
                              top: -4,
                            }}
                          >
                            {deleteCountData.deleted}
                          </p>
                        </div>
                      )}
                    </div>
                  </DSBox>
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Created At:</b>{" "}
                      {klydo.createdAt?.toLocaleString("en-GB")}
                    </DSTypography>
                  </DSBox>
                  <DSBox sx={{ mb: 3 }}>
                    <DSTypography variant="body1">
                      <b>Update At:</b>{" "}
                      {klydo.updatedAt?.toLocaleString("en-GB")}
                    </DSTypography>
                  </DSBox>
                  {klydo.public?.reviewStatus?.state ===
                    PublicationReviewStates.Approved &&
                    !!klydo.public?.reviewStatus?.updatedAt && (
                      <DSBox>
                        <DSTypography variant="body1">
                          <b>Approved At:</b>{" "}
                          {new Date(
                            klydo.public.reviewStatus.updatedAt,
                          ).toLocaleString("en-GB")}
                          {/* Otherwise this reads as a verdict on an artist
                              request, and for an admin-scheduled klydo there was
                              never a request to have a verdict on. */}
                          {isAdminScheduled && " (admin override — no request)"}
                        </DSTypography>
                      </DSBox>
                    )}
                  <DSBox sx={{ mb: 1.5 }}>
                    <div>
                      <SimpleSwitch
                        title="Generated by AI"
                        on={["", ""]}
                        default={!!klydo.metadata?.generatedByAI}
                        onClick={toggleGeneratedByAI}
                      />
                    </div>
                  </DSBox>
                  <DSBox sx={{ mb: 1.5 }}>
                    <div>
                      <SimpleSwitch
                        title="Greet"
                        on={["", ""]}
                        default={!!klydo.greet}
                        onClick={toggleGreet}
                      />
                    </div>
                  </DSBox>
                  {klydo.capabilitiesV2?.animationSound?.mode ===
                    SoundMode.FIRST_LOOP && (
                    <>
                      <DSBox sx={{ mb: 1.5 }}>
                        <div>
                          <DSTextField
                            size="small"
                            label="Frequency"
                            type="number"
                            value={
                              klydo.capabilitiesV2?.animationSound
                                ?.playOnceConfig?.frequency ?? ""
                            }
                            onChange={(e) =>
                              handlePlayOnceConfigChange({
                                frequency: e.target.value
                                  ? Number(e.target.value)
                                  : null,
                              })
                            }
                            sx={{ minWidth: 200 }}
                          />
                        </div>
                      </DSBox>
                      <DSBox sx={{ mb: 1.5 }}>
                        <div>
                          <SimpleSwitch
                            title="Random"
                            on={["", ""]}
                            default={
                              !!klydo.capabilitiesV2?.animationSound
                                ?.playOnceConfig?.random
                            }
                            onClick={(val) =>
                              handlePlayOnceConfigChange({ random: val })
                            }
                          />
                        </div>
                      </DSBox>
                    </>
                  )}
                  <DSBox sx={{ mb: 1.5 }}>
                    <div>
                      <SimpleSwitch
                        title="Alarm"
                        on={["", ""]}
                        default={!!klydo.capabilitiesV2?.alarm}
                        onClick={toggleAlarm}
                      />
                    </div>
                  </DSBox>
                </DSBox>
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Artist Rating</DSSectionHeading>
              <DSCardContent>
                <DSBox sx={{ mb: 1.5 }}>
                  <div>
                    <SimpleSwitch
                      title="Rating Klydo"
                      on={["", ""]}
                      default={!!klydo.artistsRating?.active}
                      onClick={toggleRatingKlydo}
                    />
                  </div>
                </DSBox>
                {klydo.artistsRating?.active && (
                  <>
                    <DSBox sx={{ mb: 1.5 }}>
                      <DSTextField
                        size="small"
                        label="Start date"
                        type="datetime-local"
                        value={toDateTimeLocalValue(
                          klydo.artistsRating?.startedAt,
                        )}
                        onChange={(e) =>
                          handleRatingDateChange("startedAt", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 240 }}
                      />
                    </DSBox>
                    <DSBox sx={{ mb: 1.5 }}>
                      <DSTextField
                        size="small"
                        label="End date"
                        type="datetime-local"
                        value={toDateTimeLocalValue(
                          klydo.artistsRating?.endDate,
                        )}
                        onChange={(e) =>
                          handleRatingDateChange("endDate", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 240 }}
                      />
                    </DSBox>
                  </>
                )}
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Media Information</DSSectionHeading>
              <DSCardContent>
                <DSBox>
                  <DSTypography variant="body1">
                    <b>File Name:</b> {klydo.metadata?.fileName || "N/A"}
                  </DSTypography>
                </DSBox>
                <DSBox>
                  <DSTypography variant="body1">
                    <b>Loop Duration:</b>{" "}
                    {klydo.metadata?.klydoDurationInSeconds
                      ? `${Math.floor(klydo.metadata.klydoDurationInSeconds / 60)}:${String(klydo.metadata.klydoDurationInSeconds % 60).padStart(2, "0")}`
                      : "N/A"}
                  </DSTypography>
                </DSBox>
                {klydo.metadata?.chimesDurationInSeconds && (
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Chimes Duration:</b>{" "}
                      {`${Math.floor(klydo.metadata.chimesDurationInSeconds / 60)}:${String(klydo.metadata.chimesDurationInSeconds % 60).padStart(2, "0")}`}
                    </DSTypography>
                  </DSBox>
                )}
                {klydo.metadata?.chimesFileName && (
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Chimes File:</b> {klydo.metadata.chimesFileName}
                    </DSTypography>
                  </DSBox>
                )}
                {klydo.metadata?.alarmDurationInSeconds && (
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Alarm Duration:</b>{" "}
                      {`${Math.floor(klydo.metadata.alarmDurationInSeconds / 60)}:${String(klydo.metadata.alarmDurationInSeconds % 60).padStart(2, "0")}`}
                    </DSTypography>
                  </DSBox>
                )}
                {klydo.metadata?.alarmFileName && (
                  <DSBox>
                    <DSTypography variant="body1">
                      <b>Alarm File:</b> {klydo.metadata.alarmFileName}
                    </DSTypography>
                  </DSBox>
                )}
                <DSBox>
                  <DSTypography variant="body1">
                    <b>Sound Capability:</b>{" "}
                    {klydo.capabilitiesV2?.animationSound?.mode ||
                      SoundMode.OFF}
                  </DSTypography>
                </DSBox>
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Author</DSSectionHeading>
              <DSCardContent>
                <DSBox>
                  {cahngeCreator ? (
                    <>
                      <DSTypography variant="body1" sx={{ display: "flex" }}>
                        <b
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                          }}
                        >
                          Author:
                        </b>
                        <UserDropdown
                          id={creatorId}
                          onOptionSelect={(value) => {
                            setCreatorId(value);
                          }}
                        />
                        <ButtonLoader
                          variant="outline-dark"
                          title={<CheckIcon fontSize="small" />}
                          onClick={async () => {
                            updateKlydo({ creator: creatorId });
                            setCahngeCreator(false);
                          }}
                        />
                        <DSIconButton
                          size="small"
                          onClick={() => setCahngeCreator(false)}
                        >
                          <CloseIcon fontSize="small" />
                        </DSIconButton>
                      </DSTypography>
                    </>
                  ) : (
                    <>
                      <b>Author:</b>

                      <DSTypography
                        sx={{
                          display: "inline",
                          cursor: "pointer",
                          color: "primary.main",
                          textDecoration: "underline",
                          ml: 1,
                        }}
                        onClick={() => {
                          navigate(
                            `/admin/users?search=${encodeURIComponent(creatorId)}`,
                          );
                        }}
                      >
                        {author?.name}
                      </DSTypography>
                      <FaEdit
                        size={20}
                        style={{ marginLeft: 10 }}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setCahngeCreator(true);
                        }}
                      />
                    </>
                  )}
                </DSBox>
                <DSBox sx={{ mb: 3 }}>
                  <DSTypography variant="body1">
                    <b>Creator ID:</b>
                    {creatorId}
                  </DSTypography>
                </DSBox>
                <DSBox sx={{ mb: 3 }}>
                  <DSTypography variant="body1">
                    <b>Studio Contract:</b>{" "}
                    {(() => {
                      if (!author?.studioContract) {
                        return "Didn't sign studio contract";
                      }
                      if (!author.studioContract.accepted) {
                        return "Didn't sign studio contract";
                      }
                      return `Signed on ${new Date(
                        author.studioContract.signedTimestamp!,
                      ).toLocaleString("en-GB")}`;
                    })()}
                  </DSTypography>
                </DSBox>
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Theme</DSSectionHeading>
              <DSCardContent>
                <DSBox sx={{ mb: 3 }}>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <b>Background Color:</b>
                    <Editable
                      title="color"
                      default={klydo.theme.backgroundColor}
                      onSet={(value) => updateTheme({ backgroundColor: value })}
                      editIcon={<FaEdit size={20} style={{ marginLeft: 10 }} />}
                      viewAs={
                        <span
                          className="dot"
                          style={{
                            backgroundColor: klydo.theme.backgroundColor,
                            color: getContrastYIQ(klydo.theme.backgroundColor),
                          }}
                        >
                          {klydo.theme.backgroundColor}
                        </span>
                      }
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <b>Hands Color:</b>
                    <Editable
                      title="color"
                      default={klydo.theme.handsColor}
                      onSet={(value) => updateTheme({ handsColor: value })}
                      editIcon={<FaEdit size={20} style={{ marginLeft: 10 }} />}
                      viewAs={
                        <span
                          className="dot"
                          style={{
                            backgroundColor: klydo.theme.handsColor,
                            color: getContrastYIQ(klydo.theme.handsColor),
                          }}
                        >
                          {klydo.theme.handsColor}
                        </span>
                      }
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <b>Pendulum Color:</b>
                    <Editable
                      title="color"
                      default={klydo.theme.pendulumColor}
                      onSet={(value) => updateTheme({ pendulumColor: value })}
                      editIcon={<FaEdit size={20} style={{ marginLeft: 10 }} />}
                      viewAs={
                        <span
                          className="dot"
                          style={{
                            backgroundColor: klydo.theme.pendulumColor,
                            color: getContrastYIQ(klydo.theme.pendulumColor),
                          }}
                        >
                          {klydo.theme.pendulumColor}
                        </span>
                      }
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <b>Pendulum Rod Color:</b>
                    <Editable
                      title="color"
                      default={klydo.theme.pendulumRodColor}
                      onSet={(value) =>
                        updateTheme({ pendulumRodColor: value })
                      }
                      editIcon={<FaEdit size={20} style={{ marginLeft: 10 }} />}
                      viewAs={
                        <span
                          className="dot"
                          style={{
                            backgroundColor: klydo.theme.pendulumRodColor,
                            color: getContrastYIQ(klydo.theme.pendulumRodColor),
                          }}
                        >
                          {klydo.theme.pendulumRodColor}
                        </span>
                      }
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex" }}>
                      <b>Dials Color:</b>
                      {showDials && (
                        <Editable
                          title="color"
                          default={dialsColor}
                          onSet={(value) => {
                            const theme = klydo.theme;
                            return updateKlydo({
                              theme: { ...theme, dialsColor: value },
                            });
                          }}
                          editIcon={
                            <FaEdit size={20} style={{ marginLeft: 10 }} />
                          }
                          viewAs={
                            <span
                              className="dot"
                              style={{
                                backgroundColor: dialsColor,
                                color: getContrastYIQ(dialsColor),
                              }}
                            >
                              {dialsColor}
                            </span>
                          }
                        />
                      )}
                    </div>
                    <SimpleSwitch
                      title="Dials"
                      on={undefined}
                      default={showDials}
                      onClick={() => updateKlydo({ dials: !showDials })}
                    />
                  </div>
                </DSBox>
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2 }]}>
              <DSSectionHeading>Times</DSSectionHeading>
              <DSCardContent>
                <TimeControl klydo={klydo} editable={true} />
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2, pb: 2 }]}>
              <DSSectionHeading>Tags</DSSectionHeading>
              <DSCardContent>
                <TagList klydo={klydo} onSave={(l) => updateTags(l)} />
              </DSCardContent>
            </DSCard>
            <DSCard sx={[sectionCardSx, { mb: 2, pb: 2 }]}>
              <DSSectionHeading>Licensing</DSSectionHeading>
              <DSCardContent>
                {licensingEntries.map((entry, index) => (
                  <DSBox
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 1,
                      alignItems: "flex-end",
                    }}
                  >
                    <DSBox sx={{ flex: 4 }}>
                      <DSTextField
                        label="Licensing Expiration Date"
                        type="date"
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={
                          entry.expirationDate
                            ? new Date(entry.expirationDate)
                                .toISOString()
                                .split("T")[0]
                            : ""
                        }
                        onChange={(e) => {
                          const updated = [...licensingEntries];
                          updated[index] = {
                            ...updated[index],
                            expirationDate: e.target.value,
                          };
                          setLicensingEntries(updated);
                        }}
                      />
                    </DSBox>
                    <DSBox sx={{ flex: 6 }}>
                      <DSTextField
                        label="Details"
                        type="text"
                        size="small"
                        fullWidth
                        value={entry.description || ""}
                        onChange={(e) => {
                          const updated = [...licensingEntries];
                          updated[index] = {
                            ...updated[index],
                            description: e.target.value,
                          };
                          setLicensingEntries(updated);
                        }}
                      />
                    </DSBox>
                    <DSBox sx={{ flex: 2, pb: 0.5 }}>
                      <DSErrorButton
                        size="small"
                        onClick={() => {
                          setLicensingEntries(
                            licensingEntries.filter((_, i) => i !== index),
                          );
                        }}
                      >
                        <FaTrash />
                      </DSErrorButton>
                    </DSBox>
                  </DSBox>
                ))}
                <DSBox
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <DSSecondaryButton
                    size="small"
                    onClick={() =>
                      setLicensingEntries([
                        ...licensingEntries,
                        { expirationDate: "", description: "" },
                      ])
                    }
                  >
                    Add License
                  </DSSecondaryButton>
                  <ButtonLoader
                    variant="outline-dark"
                    title="Save"
                    onClick={() => updateKlydo({ licensing: licensingEntries })}
                  />
                </DSBox>
              </DSCardContent>
            </DSCard>
          </DSBox>

          <DSBox>
            <DSBox
              sx={{
                mb: 3,
                py: 3,
                display: "flex",
                justifyContent: "center",
                background:
                  "radial-gradient(circle at 50% 40%, rgba(255,216,72,0.07) 0%, transparent 65%)",
              }}
            >
              <div
                style={{
                  width: "476px",
                  height: "785px",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "20px",
                    right: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    color: "white",
                    zIndex: 5,
                    fontWeight: "bold",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      download(klydo.loopUrl, klydo.id);
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                    <span style={{ marginLeft: "8px" }}>
                      {klydo.loopUrl.substring(
                        klydo.loopUrl.lastIndexOf(".") + 1,
                      )}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const gifUrl = klydo.loopUrl.replace(/\.mp4$/, ".gif");
                      download(gifUrl, `${klydo.id}.gif`);
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 16 }} />
                    <span style={{ marginLeft: "8px" }}>gif</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={toggleMute}
                    title={isMuted ? "Unmute audio" : "Mute audio"}
                  >
                    {isMuted ? (
                      <FaVolumeMute size={16} color="white" />
                    ) : (
                      <FaVolumeUp size={16} color="white" />
                    )}
                    <span style={{ marginLeft: "8px" }}>
                      {isMuted ? "unmute" : "mute"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={togglePause}
                    title={isPaused ? "Play video" : "Pause video"}
                  >
                    {isPaused ? (
                      <FaRegPlayCircle size={16} color="white" />
                    ) : (
                      <FaRegPauseCircle size={16} color="white" />
                    )}
                    <span style={{ marginLeft: "8px" }}>
                      {isPaused ? "play" : "pause"}
                    </span>
                  </div>
                  <DSBox
                    sx={{
                      position: "absolute",
                      top: 20,
                      right: 320,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <DSTextField
                      type="number"
                      value={displayDuration}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 30) {
                          setDisplayDuration(value);
                        }
                      }}
                      inputProps={{
                        min: 1,
                        max: 30,
                        step: 0.5,
                      }}
                      size="small"
                      sx={{
                        width: 80,
                        "& .MuiInputBase-root": {
                          backgroundColor: "background.paper",
                          fontSize: "12px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "4px 8px",
                        },
                      }}
                      placeholder="5s"
                      helperText=""
                    />
                    <DSIconButton
                      onClick={() => {
                        generatePreviewVideo({
                          klydoIds: [klydo.id],
                          // Timestamped so each render lands on its own object:
                          // the bucket serves max-age=3600, so reusing the
                          // klydo id would keep showing the previous video.
                          itemId: `${klydo.id}-${Date.now()}`,
                          pendulumLogo: klydo.pendulumLogo || undefined,
                          displayDuration: displayDuration * 1000,
                        });
                      }}
                      disabled={isGenerating}
                      title={`Generate video with ${displayDuration}s per Klydo`}
                      sx={{
                        color: KLYDO_WHITE,
                        "&:hover": {
                          transform: "scale(1.1)",
                          backgroundColor: "transparent",
                        },
                        "&:disabled": {
                          color: KLYDO_WHITE,
                        },
                      }}
                    >
                      {isGenerating ? (
                        <DSCircularProgress
                          size={25}
                          sx={{ color: KLYDO_WHITE }}
                        />
                      ) : (
                        <DownloadIcon sx={{ fontSize: 25 }} />
                      )}
                    </DSIconButton>
                    {videoError && (
                      <DSBox
                        sx={{
                          position: "absolute",
                          top: 60,
                          right: 10,
                          backgroundColor: "error.main",
                          color: "error.contrastText",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          maxWidth: "300px",
                          zIndex: 10,
                        }}
                      >
                        {videoError.message || "Failed to generate video"}
                      </DSBox>
                    )}
                    {generatedVideoUrl && (
                      <DSBox
                        sx={{
                          position: "absolute",
                          top: 60,
                          right: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          backgroundColor: "background.paper",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          zIndex: 10,
                        }}
                      >
                        <DSBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <DSTextField
                            value={generatedVideoUrl}
                            size="small"
                            InputProps={{
                              readOnly: true,
                            }}
                            sx={{
                              width: "300px",
                              "& .MuiInputBase-root": {
                                fontSize: "11px",
                              },
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                              },
                            }}
                          />
                          <DSIconButton
                            size="small"
                            onClick={handleCopyVideoUrl}
                            title="Copy video URL"
                            sx={{
                              color: videoCopied
                                ? "success.main"
                                : "primary.main",
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                          </DSIconButton>
                          {videoCopied && (
                            <DSTypography
                              sx={{
                                color: "success.main",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              Copied!
                            </DSTypography>
                          )}
                        </DSBox>
                        <DSTypography
                          sx={{
                            color: "text.secondary",
                            fontSize: "10px",
                            fontStyle: "italic",
                          }}
                        >
                          Note: URL will be valid after 5-15 minutes
                        </DSTypography>
                      </DSBox>
                    )}
                  </DSBox>
                </div>

                <ClockView
                  loopUrl={uploadResponse?.url || klydo.klydoAnimation?.l}
                  themeID={klydo.id}
                  timeShift={0}
                  handsColor={klydo.theme.handsColor}
                  pendulumColor={klydo.theme.pendulumColor}
                  pendulumRodColor={klydo.theme.pendulumRodColor}
                  backgroundColor={klydo.theme.backgroundColor}
                  dialsColor={dialsColor}
                  showDials={showDials}
                  muted={isMuted}
                  videoRef={videoRef}
                  pendulumLogo={klydo?.pendulumLogo || null}
                  pendulumLogoType={klydo?.pendulumLogoType}
                />
              </div>
            </DSBox>
            <DSBox sx={{ mb: 3 }}>
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 4,
                  alignItems: "center",
                }}
              >
                <DSBox>
                  <DSLabelTypography>Animation file</DSLabelTypography>
                  <DSInput
                    key={loopInputKey}
                    inputProps={{ accept: ".mp4, .gif" }}
                    type="file"
                    onChange={handleLoopFileChange}
                  />
                </DSBox>
                <DSPrimaryButton
                  disabled={isLoadingUrl || !uploadResponse || isUpdateingKlydo}
                  onClick={() => {
                    updateKlydo({
                      loopUrl: uploadResponse!.url,
                      metadata: {
                        fileName: uploadResponse!.fileName,
                        klydoDurationInSeconds: uploadResponse!.duration!,
                        loopUrlChecksum: uploadResponse!.checksum,
                      },
                      capabilitiesV2: {
                        ...klydo.capabilitiesV2,
                        animationSound: {
                          ...klydo.capabilitiesV2?.animationSound,
                          mode: loopSoundMode,
                        },
                      },
                    });
                  }}
                >
                  {isLoadingUrl || isUpdateingKlydo ? (
                    <DSCircularProgress />
                  ) : (
                    "Save"
                  )}
                </DSPrimaryButton>
              </DSBox>
              {errorMessage && !errorMessage && (
                <DSTypography sx={{ color: "error.main" }}>
                  {errorMessage}
                </DSTypography>
              )}
              <DSDialog
                open={!!pendingLoopFile}
                onClose={closeLoopDialog}
                fullWidth
                maxWidth="xs"
              >
                <DSDialogTitle>Upload video</DSDialogTitle>
                <DSDialogContent>
                  <DSBox
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <DSTypography>{pendingLoopFile?.name}</DSTypography>
                    <DSFormControlLabel
                      control={
                        <DSCheckBox
                          checked={normalizeLoop}
                          onChange={(e) => setNormalizeLoop(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Normalize sound for the video"
                    />
                    <DSFormControl size="small" fullWidth>
                      <DSInputLabel id="sound-mode-label">
                        Sound Mode
                      </DSInputLabel>
                      <DSSelect
                        labelId="sound-mode-label"
                        value={dialogSoundMode}
                        label="Sound Mode"
                        onChange={(e) =>
                          setDialogSoundMode(e.target.value as SoundMode)
                        }
                      >
                        <DSMenuItem value={SoundMode.OFF}>OFF</DSMenuItem>
                        <DSMenuItem value={SoundMode.ON}>ON</DSMenuItem>
                        <DSMenuItem value={SoundMode.FIRST_LOOP}>
                          FIRST_LOOP
                        </DSMenuItem>
                      </DSSelect>
                    </DSFormControl>
                  </DSBox>
                </DSDialogContent>
                <DSDialogActions>
                  <DSSecondaryButton onClick={closeLoopDialog}>
                    Cancel
                  </DSSecondaryButton>
                  <DSPrimaryButton onClick={confirmLoopUpload}>
                    Upload
                  </DSPrimaryButton>
                </DSDialogActions>
              </DSDialog>
            </DSBox>
            <DSBox sx={{ mb: 3 }}>
              <DSCard sx={[sectionCardSx, { mb: 2 }]}>
                <DSSectionHeading>
                  Chime, Strike &amp; Alarm Upload
                </DSSectionHeading>
                <DSCardContent>
                  <DSBox
                    sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                  >
                    {/* Chime section */}
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <DSTypography variant="body2">
                        <b>Chime</b>
                      </DSTypography>
                      {klydo.chimesUrls && klydo.chimesUrls.length > 0 && (
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {klydo.chimesUrls.map((url, index) => (
                            <DSBox
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AudioPlayer
                                src={url}
                                title={getChimeDisplayName(
                                  url,
                                  index,
                                  klydo?.metadata?.chimesFileName,
                                )}
                                duration={
                                  klydo?.metadata?.chimesDurationInSeconds
                                }
                                onPlay={() => handlePlay(index)}
                                onPause={() => {
                                  if (playingChime === index) {
                                    setPlayingChime(null);
                                  }
                                }}
                              />
                              <DSIconButton
                                size="small"
                                onClick={() =>
                                  download(url, `${klydo.id}-chime`)
                                }
                                title="Download chime"
                              >
                                <DownloadIcon fontSize="small" />
                              </DSIconButton>
                              <DSIconButton
                                size="small"
                                onClick={deleteChime}
                                title="Delete chime (also clears strike)"
                              >
                                <FaTrash size={16} />
                              </DSIconButton>
                            </DSBox>
                          ))}
                        </DSBox>
                      )}
                      <DSBox
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <DSFormControlLabel
                          control={
                            <DSCheckBox
                              checked={normalizeChime}
                              onChange={(e) =>
                                setNormalizeChime(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Normalize sound"
                        />
                        <DSInput
                          inputProps={{ accept: ".mp3, .wav, .m4a, .ogg" }}
                          type="file"
                          onChange={async (
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setChimeErrorMessage("");
                              setChimeUploadResponse(null);

                              try {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64 = reader.result as string;
                                  const response = await uploadChime({
                                    file: base64,
                                    fileName: file.name,
                                    fileType: file.type,
                                    normalize: normalizeChime,
                                  });

                                  if (
                                    response.status === "approved" &&
                                    response.url
                                  ) {
                                    setChimeUploadResponse({
                                      url: response.url,
                                      fileName: file.name,
                                      chimesDurationInSeconds:
                                        response.duration,
                                    });
                                  } else {
                                    setChimeErrorMessage(
                                      response.error || "Upload failed",
                                    );
                                  }
                                };
                                reader.readAsDataURL(file);
                              } catch (error) {
                                setChimeErrorMessage(
                                  "Failed to upload chime" +
                                    (error && (error as Error).message
                                      ? `: ${(error as Error).message}`
                                      : ""),
                                );
                              }
                            }
                          }}
                        />
                        <DSPrimaryButton
                          disabled={
                            isUploadingChime ||
                            !chimeUploadResponse ||
                            isUpdateingKlydo
                          }
                          onClick={() => {
                            if (chimeUploadResponse) {
                              updateKlydo({
                                chimesUrls: [chimeUploadResponse.url],
                                metadata: {
                                  ...klydo.metadata,
                                  chimesFileName: chimeUploadResponse.fileName,
                                  chimesDurationInSeconds:
                                    chimeUploadResponse.chimesDurationInSeconds,
                                },
                              });
                            }
                          }}
                        >
                          {isUploadingChime || isUpdateingKlydo ? (
                            <DSCircularProgress />
                          ) : (
                            "Save Chime"
                          )}
                        </DSPrimaryButton>
                      </DSBox>
                      {chimeErrorMessage && (
                        <DSTypography sx={{ color: "error.main" }}>
                          {chimeErrorMessage}
                        </DSTypography>
                      )}
                    </DSBox>

                    {/* Strike section */}
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <DSTypography variant="body2">
                        <b>Strike</b>{" "}
                        {klydo.chimesUrls && klydo.chimesUrls.length > 0 && (
                          <span style={{ color: "red", fontSize: "12px" }}>
                            (required)
                          </span>
                        )}
                      </DSTypography>
                      {klydo.strikesUrls && klydo.strikesUrls.length > 0 && (
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {klydo.strikesUrls.map((url, index) => (
                            <DSBox
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AudioPlayer
                                src={url}
                                title={`Strike ${index + 1}`}
                              />
                              <DSIconButton
                                size="small"
                                onClick={() =>
                                  download(url, `${klydo.id}-strike`)
                                }
                                title="Download strike"
                              >
                                <DownloadIcon fontSize="small" />
                              </DSIconButton>
                              <DSIconButton
                                size="small"
                                onClick={deleteStrike}
                                title="Delete strike"
                              >
                                <FaTrash size={16} />
                              </DSIconButton>
                            </DSBox>
                          ))}
                        </DSBox>
                      )}
                      <DSBox
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <DSFormControlLabel
                          control={
                            <DSCheckBox
                              checked={normalizeStrike}
                              onChange={(e) =>
                                setNormalizeStrike(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Normalize sound"
                        />
                        <DSInput
                          inputProps={{ accept: ".mp3, .wav, .m4a, .ogg" }}
                          type="file"
                          onChange={async (
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setStrikeErrorMessage("");
                              setStrikeUploadResponse(null);

                              try {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64 = reader.result as string;
                                  const response = await uploadStrike({
                                    file: base64,
                                    fileName: file.name,
                                    fileType: file.type,
                                    normalize: normalizeStrike,
                                  });

                                  if (
                                    response.status === "approved" &&
                                    response.url
                                  ) {
                                    setStrikeUploadResponse({
                                      url: response.url,
                                      fileName: file.name,
                                    });
                                  } else {
                                    setStrikeErrorMessage(
                                      response.error || "Upload failed",
                                    );
                                  }
                                };
                                reader.readAsDataURL(file);
                              } catch (error) {
                                setStrikeErrorMessage(
                                  "Failed to upload strike" +
                                    (error && (error as Error).message
                                      ? `: ${(error as Error).message}`
                                      : ""),
                                );
                              }
                            }
                          }}
                        />
                        <DSPrimaryButton
                          disabled={
                            isUploadingStrike ||
                            !strikeUploadResponse ||
                            isUpdateingKlydo
                          }
                          onClick={() => {
                            if (strikeUploadResponse) {
                              updateKlydo({
                                strikesUrls: [strikeUploadResponse.url],
                              });
                            }
                          }}
                        >
                          {isUploadingStrike || isUpdateingKlydo ? (
                            <DSCircularProgress />
                          ) : (
                            "Save Strike"
                          )}
                        </DSPrimaryButton>
                      </DSBox>
                      {strikeErrorMessage && (
                        <DSTypography sx={{ color: "error.main" }}>
                          {strikeErrorMessage}
                        </DSTypography>
                      )}
                    </DSBox>

                    {/* Alarm section */}
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <DSTypography variant="body2">
                        <b>Alarm</b>
                      </DSTypography>
                      {klydo.alarmUrls && klydo.alarmUrls.length > 0 && (
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {klydo.alarmUrls.map((url, index) => (
                            <DSBox
                              key={index}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AudioPlayer
                                src={url}
                                title={
                                  klydo?.metadata?.alarmFileName ||
                                  `Alarm ${index + 1}`
                                }
                                duration={
                                  klydo?.metadata?.alarmDurationInSeconds
                                }
                              />
                              <DSIconButton
                                size="small"
                                onClick={() =>
                                  download(url, `${klydo.id}-alarm`)
                                }
                                title="Download alarm"
                              >
                                <DownloadIcon fontSize="small" />
                              </DSIconButton>
                              <DSIconButton
                                size="small"
                                onClick={deleteAlarm}
                                title="Delete alarm"
                              >
                                <FaTrash size={16} />
                              </DSIconButton>
                            </DSBox>
                          ))}
                        </DSBox>
                      )}
                      <DSBox
                        sx={{ display: "flex", flexDirection: "row", gap: 2 }}
                      >
                        <DSInput
                          inputProps={{ accept: ".mp3, .wav, .m4a, .ogg" }}
                          type="file"
                          onChange={async (
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setAlarmErrorMessage("");
                              setAlarmUploadResponse(null);

                              try {
                                const reader = new FileReader();
                                reader.onload = async () => {
                                  const base64 = reader.result as string;
                                  const response = await uploadAlarm({
                                    file: base64,
                                    fileName: file.name,
                                    fileType: file.type,
                                  });

                                  if (
                                    response.status === "approved" &&
                                    response.url
                                  ) {
                                    setAlarmUploadResponse({
                                      url: response.url,
                                      fileName: file.name,
                                      alarmDurationInSeconds: response.duration,
                                    });
                                  } else {
                                    setAlarmErrorMessage(
                                      response.error || "Upload failed",
                                    );
                                  }
                                };
                                reader.readAsDataURL(file);
                              } catch (error) {
                                setAlarmErrorMessage(
                                  "Failed to upload alarm" +
                                    (error && (error as Error).message
                                      ? `: ${(error as Error).message}`
                                      : ""),
                                );
                              }
                            }
                          }}
                        />
                        <DSPrimaryButton
                          disabled={
                            isUploadingAlarm ||
                            !alarmUploadResponse ||
                            isUpdateingKlydo
                          }
                          onClick={() => {
                            if (alarmUploadResponse) {
                              updateKlydo({
                                alarmUrls: [alarmUploadResponse.url],
                                metadata: {
                                  ...klydo.metadata,
                                  alarmFileName: alarmUploadResponse.fileName,
                                  alarmDurationInSeconds:
                                    alarmUploadResponse.alarmDurationInSeconds,
                                },
                              });
                            }
                          }}
                        >
                          {isUploadingAlarm || isUpdateingKlydo ? (
                            <DSCircularProgress />
                          ) : (
                            "Save Alarm"
                          )}
                        </DSPrimaryButton>
                      </DSBox>
                      {alarmErrorMessage && (
                        <DSTypography sx={{ color: "error.main" }}>
                          {alarmErrorMessage}
                        </DSTypography>
                      )}
                    </DSBox>
                  </DSBox>
                </DSCardContent>
              </DSCard>
              <PendulumCard
                pendulumLogoUrl={klydo.pendulumLogo || undefined}
                pendulumLogoHoldStatus={klydo.holdPendulumLogo || false}
                pendulumLogoType={klydo.pendulumLogoType}
                klydoId={klydo.id}
              />
            </DSBox>
            <DSPrimaryButton
              sx={{ mt: 2, mr: 1 }}
              onClick={async () => {
                try {
                  setShareLoading(true);
                  setShareSuccess(false);

                  const { id: shareKey } =
                    await adminApiClient.getKlydoShareKey(klydo.id);

                  const shareUrl = `https://studio.klydoclock.com/public/klydo/${shareKey}`;

                  await navigator.clipboard.writeText(shareUrl);

                  setShareSuccess(true);
                  setTimeout(() => setShareSuccess(false), 3000);
                } catch (error) {
                  console.error("Error sharing klydo:", error);
                } finally {
                  setShareLoading(false);
                }
              }}
            >
              <ShareIcon sx={{ mr: 0.5, fontSize: 18 }} />
              {shareLoading
                ? "Sharing..."
                : shareSuccess
                  ? "Copied!"
                  : "Copy Share Link"}
            </DSPrimaryButton>

            <DSSecondaryButton
              sx={{ mt: 2, ml: 0.5 }}
              onClick={() => setShowFrames(true)}
            >
              Show All Frames
            </DSSecondaryButton>
            <DSSecondaryButton
              sx={{ mt: 2, ml: 0.5 }}
              onClick={() => setShowChooseFrames(true)}
            >
              Choose Frames
            </DSSecondaryButton>
            <DSBox>
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "15px",
                  }}
                >
                  <b>{" Unlist: "}</b>
                  <ConfirmationSwitch
                    status={!!klydo.unlisted}
                    action={toggleUnlistedStatus}
                    onMessage="Are you sure... this will remove the klydo to all clocks"
                    offMessage="This will make the Klydo available for all actions (pool | public)."
                    disabledReason={isUnlistedValidAction()}
                  />
                </div>
              </>
            </DSBox>
            <DSBox>
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "15px",
                  }}
                >
                  <b>{" Public: "}</b>
                  <ConfirmationSwitch
                    status={isPublicOrScheduled}
                    action={togglePublicStatus}
                    onMessage="Are you sure you want to turn this Klydo to public?"
                    offMessage="Are you sure you want to turn this Klydo to private?"
                    disabledReason={isPublicdValidAction()}
                  />
                  {!!requestedPublishTimestamp && (
                    <Schedule
                      style={{ marginLeft: "15px", fontSize: "20spx" }}
                      onClick={() =>
                        setPublishDateTypeDialog(PublishType.PUBLIC)
                      }
                    />
                  )}

                  {!!requestedPublishTimestamp && (
                    <span style={{ marginLeft: "15px", fontSize: "14px" }}>
                      {isPublic ? "Published on: " : "Will be public on: "}
                      {new Date(requestedPublishTimestamp).toLocaleDateString(
                        "en-GB",
                      )}
                    </span>
                  )}

                  {isAdminScheduled && (
                    <DSChip
                      size="small"
                      label="Admin-scheduled"
                      title={
                        "Set by an admin. This Klydo never made a publication " +
                        "request — the approval on it is an admin override, not " +
                        "a verdict on something the artist asked for."
                      }
                      sx={{ marginLeft: "10px" }}
                    />
                  )}
                </div>
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <DSTooltip
                    title={
                      // A klydo whose approved go-live date is still ahead is a
                      // legitimate member of a LATER event — the api's own event
                      // check says so explicitly. Requiring `isPublic` here was
                      // stricter than the server and blocked exactly the case it
                      // was designed for.
                      goLiveDate
                        ? `Goes public on ${goLiveDate.toLocaleDateString("en-GB")}, so it can only be scheduled from the next day on`
                        : !isPublic
                          ? "Klydo cannot be added to the scheduler if it is not public or scheduled to become public"
                          : ""
                    }
                  >
                    <span>
                      <DSPrimaryButton
                        variant="outlined"
                        size="small"
                        disabled={!isPublic && !willBecomePublic}
                        onClick={() => setShowAddToKlydoEvent(true)}
                      >
                        Add to Scheduler
                      </DSPrimaryButton>
                    </span>
                  </DSTooltip>
                  {scheduledEvent && (
                    <>
                      <DSTypography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {`Already in scheduler — scheduled for ${scheduledEventDate}${scheduledEvent.name ? ` ("${scheduledEvent.name}")` : ""}`}
                      </DSTypography>
                      <DSSecondaryButton
                        variant="outlined"
                        size="small"
                        disabled={isRemovingFromScheduler}
                        onClick={() => setShowRemoveFromScheduler(true)}
                      >
                        {isRemovingFromScheduler
                          ? "Removing..."
                          : "Remove From Scheduler"}
                      </DSSecondaryButton>
                    </>
                  )}
                  {removeFromSchedulerError && (
                    <DSTypography
                      variant="caption"
                      sx={{ color: "error.main" }}
                    >
                      {removeFromSchedulerError}
                    </DSTypography>
                  )}
                </DSBox>
              </>
            </DSBox>
            {klydo.type === KlydoType.Draft && (
              <>
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DSChip
                      label="Status: Draft (not sealed)"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                    <DSTypography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      Until it is sealed, only dev, artist and rater clocks can
                      receive this klydo.
                    </DSTypography>
                  </DSBox>
                  <DSPrimaryButton
                    variant="outlined"
                    size="small"
                    disabled={isSealing}
                    onClick={() => setShowSealConfirmation(true)}
                  >
                    {isSealing ? "Sealing..." : "Seal Klydo"}
                  </DSPrimaryButton>
                  {sealError && (
                    <DSTypography
                      variant="caption"
                      sx={{ color: "error.main" }}
                    >
                      {sealError}
                    </DSTypography>
                  )}
                </DSBox>
                <ConfirmationModal
                  open={showSealConfirmation}
                  message={
                    "Sealing is permanent: the klydo becomes final, its assets " +
                    "move to the permanent folders, and the artist can no " +
                    "longer edit it as a draft. It does NOT publish it or " +
                    "approve its publication request. Seal this klydo?"
                  }
                  onClose={() => setShowSealConfirmation(false)}
                  onConfirm={async () => {
                    setShowSealConfirmation(false);
                    setSealError(null);
                    try {
                      await sealKlydo();
                    } catch (error) {
                      setSealError(
                        extractApiErrorMessage(
                          error,
                          "Failed to seal the klydo",
                        ),
                      );
                    }
                  }}
                />
              </>
            )}
            <DSBox>
              {publishDateTypeDialog && (
                <PublishDateDialog
                  klydo={klydo}
                  open={!!publishDateTypeDialog}
                  type={publishDateTypeDialog}
                  onClose={() => setPublishDateTypeDialog("")}
                />
              )}
            </DSBox>
          </DSBox>
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSButton
          variant="text"
          type="button"
          onClick={() => setShowModal(false)}
        >
          OK
        </DSButton>
      </DSDialogActions>

      {showAddToKlydoEvent && (
        <UnifiedCalendarEventModal
          open={showAddToKlydoEvent}
          onClose={() => setShowAddToKlydoEvent(false)}
          initialKlydoIds={[klydo.id]}
          initialPrimaryType="Klydo Event"
          minDate={earliestSchedulableDate}
        />
      )}

      {showRemoveFromScheduler && scheduledEvent && (
        <ConfirmationModal
          open={showRemoveFromScheduler}
          message={`Remove this klydo from the scheduler on ${scheduledEventDate}?`}
          onClose={() => setShowRemoveFromScheduler(false)}
          onConfirm={handleRemoveFromScheduler}
        />
      )}

      <>
        {showFrames && (
          <KlydoFrames
            onHide={() => setShowFrames(false)}
            klydoLoopUrl={klydo.loopUrl}
          />
        )}
        {showChooseFrames && (
          <ChooseFramesDialog
            onHide={() => setShowChooseFrames(false)}
            animationUrl={klydo.loopUrl}
          />
        )}
      </>
    </>
  );
}

const KlydoModalWrapper = (props: KlydoModalWrapperProps) => {
  const { data: klydo, isLoading } = useKlydo(props.klydoId || props.row.id);
  if (isLoading || !klydo) return <DSCircularProgress />;
  return <KlydoModal {...props} klydo={klydo} />;
};

export default KlydoModalWrapper;
